import { createFileRoute, Link } from "@tanstack/react-router";
import { Waves, ArrowLeft, Wind, Sparkles, Activity } from "lucide-react";
import { BookmarkButton } from "@/components/learn/BookmarkButton";
import { LearnQA } from "@/components/learn/LearnQA";
import { LearnCarousel, LEARN_THEMES, type LearnCard } from "@/components/learn/LearnCarousel";
import {
  WOT_INTRO,
  WOT_HYPER_SIGNS,
  WOT_WINDOW_SIGNS,
  WOT_HYPO_SIGNS,
  WOT_WHY_NARROWS,
  WOT_RETURN_STEPS,
} from "@/data/learn/window-of-tolerance";

export const Route = createFileRoute("/learn/window-of-tolerance")({
  head: () => ({
    meta: [
      { title: "Window of Tolerance Explained | Recovery Trauma™" },
      { name: "description", content: "What the window of tolerance is, how to spot hyperarousal and hypoarousal, and gentle ways to regulate your nervous system and widen your window again." },
      { property: "og:title", content: "Window of Tolerance — Recovery Trauma™" },
      { property: "og:description", content: "The nervous system, explained gently. Learn how trauma narrows your window — and how safe practices widen it." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://recoverytrauma.com/learn/window-of-tolerance" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/window-of-tolerance" }],
  }),
  component: WindowOfTolerancePage,
});

function WindowDiagram() {
  return (
    <svg
      viewBox="0 0 400 220"
      role="img"
      aria-label="Three horizontal bands: red hyperarousal on top, green window of tolerance in the middle, blue hypoarousal on the bottom."
      className="mx-auto h-auto w-full max-w-sm rounded-2xl"
    >
      <rect x="0" y="0" width="400" height="60" fill="hsl(0 65% 92%)" />
      <text x="20" y="35" fill="hsl(0 55% 35%)" fontSize="14" fontWeight="600">🔥 Hyperarousal</text>
      <rect x="0" y="70" width="400" height="80" fill="hsl(150 45% 90%)" />
      <text x="20" y="105" fill="hsl(150 40% 25%)" fontSize="14" fontWeight="700">🌿 Window of Tolerance</text>
      <text x="20" y="125" fill="hsl(150 30% 30%)" fontSize="11">Feel · Think · Connect</text>
      <rect x="0" y="160" width="400" height="60" fill="hsl(220 55% 92%)" />
      <text x="20" y="195" fill="hsl(220 50% 30%)" fontSize="14" fontWeight="600">❄️ Hypoarousal</text>
    </svg>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5 text-start">
      {items.map((s) => (
        <li key={s} className="relative ps-5 before:absolute before:start-0 before:content-['•']">
          {s}
        </li>
      ))}
    </ul>
  );
}

function WindowOfTolerancePage() {
  const theme = LEARN_THEMES.sage;

  const cards: LearnCard[] = [
    {
      id: "intro",
      emoji: "🌿",
      chip: "Intro",
      title: "The Window of Tolerance",
      body: (
        <>
          <p>{WOT_INTRO}</p>
          <WindowDiagram />
        </>
      ),
    },
    {
      id: "hyper",
      emoji: "🔥",
      chip: "Above the window",
      title: "Signs of hyperarousal",
      body: (
        <>
          <p className="text-center italic text-muted-foreground">Too much — fight or flight</p>
          <BulletList items={WOT_HYPER_SIGNS} />
        </>
      ),
    },
    {
      id: "window",
      emoji: "🌊",
      chip: "Inside the window",
      title: "Signs you're in your window",
      body: (
        <>
          <p className="text-center italic text-muted-foreground">The zone where life feels workable</p>
          <BulletList items={WOT_WINDOW_SIGNS} />
        </>
      ),
    },
    {
      id: "hypo",
      emoji: "❄️",
      chip: "Below the window",
      title: "Signs of hypoarousal",
      body: (
        <>
          <p className="text-center italic text-muted-foreground">Too little — freeze or shutdown</p>
          <BulletList items={WOT_HYPO_SIGNS} />
        </>
      ),
    },
    {
      id: "why",
      emoji: "🧠",
      chip: "Why",
      title: "Why trauma narrows the window",
      body: <p>{WOT_WHY_NARROWS}</p>,
    },
    {
      id: "return-hyper",
      emoji: "🌬️",
      chip: "Coming back",
      title: WOT_RETURN_STEPS[0].label,
      body: <BulletList items={WOT_RETURN_STEPS[0].steps} />,
    },
    {
      id: "return-hypo",
      emoji: "☀️",
      chip: "Coming back",
      title: WOT_RETURN_STEPS[1].label,
      body: <BulletList items={WOT_RETURN_STEPS[1].steps} />,
    },
    {
      id: "practise",
      emoji: "✨",
      chip: "Practise",
      title: "Widen your window over time",
      body: (
        <>
          <p className="text-center">These tools inside Recovery Trauma™ are designed to gently widen your window.</p>
          <div className="flex flex-wrap justify-center gap-2 pt-2">
            <Link to="/breathing" className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-sm text-foreground hover:bg-white dark:bg-black/30 dark:hover:bg-black/50">
              <Wind className="h-3.5 w-3.5" /> Breathing
            </Link>
            <Link to="/grounding" className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-sm text-foreground hover:bg-white dark:bg-black/30 dark:hover:bg-black/50">
              <Activity className="h-3.5 w-3.5" /> Grounding
            </Link>
            <Link to="/tapping" className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-sm text-foreground hover:bg-white dark:bg-black/30 dark:hover:bg-black/50">
              <Sparkles className="h-3.5 w-3.5" /> EFT tapping
            </Link>
            <Link to="/learn/dbt" className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-sm text-foreground hover:bg-white dark:bg-black/30 dark:hover:bg-black/50">
              <Sparkles className="h-3.5 w-3.5" /> DBT skills
            </Link>
          </div>
        </>
      ),
    },
    {
      id: "reflect",
      emoji: "💭",
      chip: "Reflection",
      title: "Where are you right now?",
      body: (
        <ul className="space-y-2 text-start">
          <li>🌿 Where in your body do you feel this moment — chest, jaw, belly, shoulders?</li>
          <li>🔥 What tips you above your window most often?</li>
          <li>❄️ What pulls you below it?</li>
          <li>✨ Which one small practice has helped you come back before?</li>
        </ul>
      ),
    },
  ];

  return (
    <main className={`min-h-dvh px-4 py-10 ${theme.pageBg}`}>
      <div className="mx-auto max-w-3xl">
        <Link to="/learn" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Learn
        </Link>

        <header className="mb-8 text-center">
          <div className={`mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${theme.chipBg} ${theme.chipText}`}>
            <Waves className="h-3.5 w-3.5" /> Guide
          </div>
          <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
            The Window of Tolerance
          </h1>
          <div className="mt-4 flex justify-center">
            <BookmarkButton id="learn:window-of-tolerance" title="Window of Tolerance" href="/learn/window-of-tolerance" section="Learn" />
          </div>
        </header>

        <LearnCarousel cards={cards} theme={theme} storageKey="wot" />

        <LearnQA
          items={[
            {
              q: "What is the window of tolerance?",
              a: "The window of tolerance is the range of arousal in which you can feel, think and connect at the same time. Inside it, emotion is workable. Above it you are in fight or flight; below it you are in shutdown. The term comes from Dan Siegel and is widely used in trauma-informed work.",
            },
            {
              q: "What does it mean to be outside your window of tolerance?",
              a: "Above the window looks like racing thoughts, a pounding heart, irritability, panic or an urge to run. Below it looks like numbness, heaviness, blankness, exhaustion or feeling far away from yourself. Neither is a failure of willpower — both are the nervous system doing what it learned to do.",
            },
            {
              q: "Why is my window of tolerance so narrow?",
              a: "Repeated overwhelming experiences teach the nervous system to react sooner and recover more slowly, so smaller things tip you out. Ongoing stress, poor sleep, illness, pain and isolation narrow it further. It is a learned response, which is also why it can widen again.",
            },
            {
              q: "How do you widen your window of tolerance?",
              a: "Small and often does more than long sessions: extended-exhale breathing, grounding, slow movement, sleep, and time with people whose presence settles you. Approaching difficult feelings in small doses and then returning to something steadying builds capacity without flooding.",
            },
            {
              q: "Is the window of tolerance the same as the polyvagal ladder?",
              a: "They describe overlapping territory in different language. The window of tolerance describes a range of arousal; the polyvagal model describes three states — safe and social, mobilised, and shut down. Many people find the two useful together.",
            },
          ]}
          links={[
            { label: "How to widen your window of tolerance", to: "/blog/how-to-widen-your-window-of-tolerance" },
            { label: "How to regulate your nervous system after trauma", to: "/blog/how-to-regulate-your-nervous-system-after-trauma" },
            { label: "The polyvagal ladder, explained gently", to: "/learn/polyvagal-ladder" },
            { label: "The freeze response and feeling stuck", to: "/blog/freeze-response-trauma-stuck" },
            { label: "Hypervigilance and always feeling on edge", to: "/blog/hypervigilance-why-you-always-feel-on-edge" },
            { label: "Depersonalisation vs derealisation", to: "/blog/depersonalisation-vs-derealisation" },
            { label: "50 grounding techniques for anxiety, PTSD and CPTSD", to: "/blog/50-grounding-techniques-for-anxiety-ptsd-cptsd" },
          ]}
        />

        <p className="mt-10 rounded-2xl border border-border bg-muted/30 p-5 text-center text-xs leading-relaxed text-muted-foreground">
          Educational only. If you feel unsafe, please reach out to <Link to="/crisis" className="text-gold underline-offset-2 hover:underline">Crisis Resources</Link>.
        </p>
      </div>
    </main>
  );
}
